import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { INPUT_CLS } from "@/lib/analysis";
import { api } from "@/lib/api";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * Email→project routing: map a sender address or domain to a project so a
 * forwarded client email is analysed with that project's context. Forwarded
 * mail comes "from me", so the match runs against any address in the thread.
 */

type Project = { id: string; name: string };
type EmailMap = { id: string; pattern: string; projectId: string; project: { name: string } };

export function EmailRouting() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [maps, setMaps] = useState<EmailMap[]>([]);
  const [pattern, setPattern] = useState("");
  const [project, setProject] = useState("");
  const [error, setError] = useState<string | null>(null);

  const loadMaps = useCallback(
    () =>
      api
        .get<{ maps: EmailMap[] }>("/api/auto-sources/email-maps")
        .then((r) => setMaps(r.maps))
        .catch(() => setError(t("analysisTools.emailRouting.loadFailed"))),
    [t],
  );

  useEffect(() => {
    api
      .get<{ projects: Project[] }>("/api/projects?pageSize=200")
      .then((r) => setProjects(r.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {});
    void loadMaps();
  }, [loadMaps]);

  async function add() {
    setError(null);
    const p = pattern.trim();
    if (!p || !project) {
      setError(t("analysisTools.emailRouting.needBoth"));
      return;
    }
    try {
      await api.post("/api/auto-sources/email-maps", { pattern: p, projectId: project });
      setPattern("");
      setProject("");
      await loadMaps();
    } catch {
      setError(t("analysisTools.emailRouting.addFailed"));
    }
  }

  async function remove(id: string) {
    await api.delete(`/api/auto-sources/email-maps/${id}`).catch(() => {});
    await loadMaps();
  }

  return (
    <Card>
      <CardContent className="space-y-4 py-5">
        <p className="text-sm text-muted-foreground">
          <Trans i18nKey="analysisTools.emailRouting.intro" components={{ code: <code /> }} />
        </p>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex flex-wrap gap-2" data-testid="add-email-map">
          <input
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder={t("analysisTools.emailRouting.patternPlaceholder")}
            className={`${INPUT_CLS} flex-1 min-w-48`}
            data-testid="email-map-pattern"
          />
          <Select
            className="w-48"
            data-testid="email-map-project"
            value={project}
            onChange={setProject}
            options={[
              { value: "", label: t("analysisTools.emailRouting.pickProject") },
              ...projects.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
          <Button type="button" onClick={add} data-testid="email-map-add">
            {t("analysisTools.emailRouting.add")}
          </Button>
        </div>

        {maps.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="email-maps-empty">
            {t("analysisTools.emailRouting.none")}
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {maps.map((m) => (
              <li
                key={m.id}
                className="flex items-center justify-between gap-3 px-3 py-2"
                data-testid="email-map-row"
              >
                <span className="text-sm">
                  <span className="font-medium">{m.pattern}</span>
                  <span className="text-muted-foreground"> → {m.project.name}</span>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(m.id)}
                  data-testid="email-map-remove"
                >
                  {t("analysisTools.emailRouting.remove")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
